import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { UiButtonDirective, UiButtonTone } from '../button/button.directive';

export type UiSaveButtonState = 'ready' | 'saving' | 'success' | 'saved';

@Component({
  selector: 'app-ui-save-button',
  imports: [UiButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
    '[attr.data-density]': "compact() ? 'compact' : null",
  },
  styles: `
    /* Tight toolbars (e.g. the phone budgets edit bar): drop the wide padding and
       the label floor; the stacked state layers still keep the width steady. */
    :host([data-density='compact']) button {
      padding-inline: calc(var(--spacing) * 3);
    }

    :host([data-density='compact']) .save-layers {
      min-width: 0;
    }

    .save-state {
      grid-area: 1 / 1;
      display: grid;
      min-height: 20px;
      place-items: center;
      line-height: 1;
      opacity: 0;
      transition: opacity 120ms ease;
    }

    button[data-save-state='ready'] .save-ready,
    button[data-save-state='saving'] .save-saving,
    button[data-save-state='success'] .save-success,
    button[data-save-state='saved'] .save-saved {
      opacity: 1;
    }

    .save-spinner,
    .save-check {
      display: block;
      width: 20px;
      height: 20px;
    }

    .save-spinner {
      box-sizing: border-box;
      border: 2px solid currentColor;
      border-right-color: transparent;
      border-radius: 9999px;
      animation: save-button-spin 650ms linear infinite;
      animation-play-state: paused;
    }

    button[data-save-state='saving'] .save-spinner {
      animation-play-state: running;
    }

    .save-check {
      overflow: visible;
    }

    .save-check-path {
      fill: none;
      stroke: currentColor;
      stroke-width: 2.4;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-dasharray: 1;
      stroke-dashoffset: 1;
    }

    button[data-save-state='success'] .save-check-path {
      animation: save-check-draw 240ms cubic-bezier(0.2, 0.8, 0.2, 1) 40ms forwards;
    }

    @keyframes save-button-spin {
      to {
        transform: rotate(360deg);
      }
    }

    @keyframes save-check-draw {
      to {
        stroke-dashoffset: 0;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .save-state {
        transition: none;
      }

      .save-spinner {
        animation-duration: 1200ms;
      }

      button[data-save-state='success'] .save-check-path {
        animation: none;
        stroke-dashoffset: 0;
      }
    }
  `,
  template: `
    <button
      appUiButton
      type="submit"
      [tone]="tone()"
      class="w-full"
      [disabled]="disabled() || state() !== 'ready'"
      [attr.aria-busy]="state() === 'saving' ? 'true' : null"
      [attr.aria-label]="label()"
      [attr.data-save-state]="state()"
    >
      <span class="save-layers grid min-w-[74px] place-items-center" aria-hidden="true">
        <span class="save-state save-ready">Save</span>
        <span class="save-state save-saving">
          <span class="save-spinner"></span>
        </span>
        <span class="save-state save-success">
          <svg class="save-check" viewBox="0 0 24 24" aria-hidden="true">
            <path class="save-check-path" pathLength="1" d="M4 12 9 17 20 6" />
          </svg>
        </span>
        <span class="save-state save-saved">Saved</span>
      </span>
    </button>
  `,
})
export class UiSaveButtonComponent {
  readonly state = input<UiSaveButtonState>('saved');
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly tone = input<UiButtonTone>('primary');
  readonly compact = input(false, { transform: booleanAttribute });

  readonly label = computed(() => {
    switch (this.state()) {
      case 'ready':
        return 'Save';
      case 'saving':
        return 'Saving';
      default:
        return 'Saved';
    }
  });
}
